import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { Account } from '../accounts.iface';
import { formatDate, nowUnixMs } from '../../../shared/utils/date.utils';
import { environment } from '../../../../environments/environment';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { assignModalData } from '../../../shared/utils/modal-data.util';
import {
  AccountFormValidationData,
  AccountsValidationService,
  AccountValidationError,
} from '../accounts.validation.service';
import { NotificatorService } from '../../../shared/services/notificator.service';
import { AccountsService } from '../accounts.service';
import { AuthService } from '../../../shared/services/auth.service';
import { UserData } from '../../../shared/interfaces/userLogin';

const ALL_ACCOUNT_ROLES = [
  {
    title: 'Super Administrator',
    acl: 'LEVEL_0',
    isCompany: false,
  },
  {
    title: 'Company',
    acl: 'LEVEL_1',
    isCompany: false,
  },
  {
    title: 'Vendor Administrator',
    acl: 'LEVEL_1',
    isCompany: true,
  },
  {
    title: 'User',
    acl: 'LEVEL_2',
    isCompany: false,
  },
];

@Component({
  selector: 'app-account-details',
  templateUrl: 'accounts.details.component.html',
  styleUrls: ['accounts.details.component.css'],
  standalone: false,
})
export class AccountsDetailsComponent implements OnInit {
  @Input() accounts?: Account[];
  @Input() account?: Account;
  @Input() editMode?: boolean;
  @Output() sumbit = new EventEmitter();
  // View Mode By User-ID
  @Input() uid?: string;
  pwdExpired: boolean;
  pwdExpDate: string;
  isVendorAdmin = false;
  isCompanyUser = false;
  currentUser: UserData;
  form = {
    // Left Side
    name: null as string | null,
    role: null as typeof ALL_ACCOUNT_ROLES[number] | null,
    //   {
    //   title: 'Super Administrator',
    //   acl: 'LEVEL_0',
    //   isCompany: false
    // },
    login: null as string | null,
    title: null as string | null, // Job Title
    adminStatus: true,
    address: {
      address1: null as string | null,
      address2: null as string | null,
      city: null as string | null,
      state: null as string | null,
      zipCode: null as string | null,
    },
    company: null as Account | null,
    email: null as string | null,
    pwd1: null as string | null,
    pwd2: null as string | null,
    // Right side
    contacts: {
      phone: null as string | null,
      fax: null as string | null,
    },
    level: null as number | null,
    allotNotify: false, // Email when property's daily allotment falls to a certain level
    orderNotify: false, // Email when properties receive an order
    roles: [...ALL_ACCOUNT_ROLES],
    rolesHandler: (_evt: typeof ALL_ACCOUNT_ROLES[number]) => {
      // bound in ngOnInit
    },
  };

  cancel() {
    this.modal.close();
  }

  addAccount() {
    this.VS.test(this.form as unknown as AccountFormValidationData, this.editMode || !!this.uid).then(
      (ok) => {
        const TS = {
          adminId: this.form.login, // User Login
          administrator: this.form.name, // Name
          title: this.form.title, // Job Title
          address: this.form.address.address1, // Address Line 1
          address2: this.form.address.address2, // Address Line 2
          city: this.form.address.state, // City
          state: this.form.address.state, // State
          zipCode: this.form.address.zipCode, // Zip
          phone: this.form.contacts.phone, // Phone
          fax: this.form.contacts.fax, // Fax
          email: this.form.email, // Email
          adminStatus: this.form.adminStatus, // Active
          allotNotify: this.form.allotNotify,
          allotLevel: this.form.level,
          orderNotify: this.form.orderNotify,
          // LEVEL_0 = Super Administrator,
          // LEVEL_1 без parent = Company,
          // LEVEL_1 с parent = Vendor Administrator,
          // LEVEL_2 с parent = User
          accessLevel: this.form.role?.acl, // Role
          parent: this.resolveParentForNewAccount(),
          // parent: null, // Company - идентификатор adminId аккаунта комании
          passWord: this.form.pwd1, // Пароль
          markup: { type: '$', value: 0 },
        };
        if (this.editMode || this.uid) {
          TS.accessLevel = this.account.accessLevel;
          TS.adminId = this.account.adminId;
          TS.markup = this.account.markup;
        }
        if (this.uid) {
          this.SRV.editAccount(TS).then((res) => {
            this.sumbit.emit(res);
          });
        } else {
          this.sumbit.emit(TS);
        }
      },
      (error) => {
        error.forEach((e: AccountValidationError) => {
          this.notify.createNotification('error', 'Validation Error', e.text, 5);
        });
      },
    );
  }

  private isVendorAdminUser(user: UserData): boolean {
    return user?.type === 'vendor' || (user?.accessLevel === 'LEVEL_1' && !!user?.parent);
  }

  private isCompanyAccountUser(user: UserData): boolean {
    return user?.type === 'company' || (user?.accessLevel === 'LEVEL_1' && !user?.parent);
  }

  private setupRolesForCreator(user: UserData): void {
    if (this.editMode || this.uid) {
      return;
    }
    if (this.isVendorAdminUser(user)) {
      this.form.roles = ALL_ACCOUNT_ROLES.filter(
        (r) => r.acl !== 'LEVEL_0' && r.title !== 'Company',
      );
    } else if (this.isCompanyAccountUser(user)) {
      this.form.roles = ALL_ACCOUNT_ROLES.filter((r) => r.acl !== 'LEVEL_0');
    } else {
      this.form.roles = [...ALL_ACCOUNT_ROLES];
    }
  }

  private getOwnCompanyAccount(): Account {
    const companyId = this.isCompanyUser
      ? this.currentUser?.adminId
      : this.currentUser?.parent || this.currentUser?.companyId;
    return this.accounts?.find((a) => a.adminId === companyId);
  }

  onRoleChange(_role: (typeof ALL_ACCOUNT_ROLES)[0]): void {
    if (this.editMode || this.uid) {
      return;
    }
    if (!this.isVendorAdmin && !this.isCompanyUser) {
      return;
    }
    if (this.form.role?.isCompany) {
      this.form.company = this.getOwnCompanyAccount() ?? null;
    } else if (this.form.role?.acl === 'LEVEL_2') {
      this.form.company = null;
    }
  }

  private resolveParentForNewAccount(): string {
    if (this.editMode || this.uid) {
      return this.form.company?.adminId;
    }
    if (this.isVendorAdmin) {
      if (this.form.role?.acl === 'LEVEL_2') {
        return this.currentUser?.adminId;
      }
      if (this.form.role?.isCompany) {
        return this.currentUser?.parent || this.currentUser?.companyId;
      }
    }
    if (this.isCompanyUser) {
      if (this.form.role?.acl === 'LEVEL_2' || this.form.role?.isCompany) {
        return this.currentUser?.adminId;
      }
      if (this.form.role?.title === 'Company') {
        return null;
      }
    }
    return this.form.company?.adminId;
  }

  initEditMode() {
    this.form.name = this.account.administrator;
    this.form.role = null; // TODO Later
    this.form.login = this.account.adminId;
    this.form.adminStatus = this.account.adminStatus;
    this.form.title = this.account.title;
    this.form.company = this.account.parent
      ? this.accounts.find((f) => this.account.parent === f.adminId)
      : null;
    this.form.address.address1 = this.account.address;
    this.form.address.address2 = this.account.address2;
    this.form.address.city = this.account.city;
    this.form.address.state = this.account.state;
    this.form.address.zipCode = this.account.zipCode;
    this.form.email = this.account.email;
    this.form.pwd1 = null;
    this.form.pwd2 = null;
    this.form.contacts.phone = this.account.phone;
    this.form.contacts.fax = this.account.fax;
    this.form.level = this.account.allotLevel;
    this.form.allotNotify = this.account.allotNotify;
    this.form.orderNotify = this.account.orderNotify;

    // Check Password Expiration
    this.pwdExpired = this.account.passwordExpired < nowUnixMs();
    this.pwdExpDate = formatDate(this.account.passwordExpired, environment.fullDateFormat);
  }
  constructor(
    private VS: AccountsValidationService,
    private SRV: AccountsService,
    private notify: NotificatorService,
    private modal: NzModalRef,
    private auth: AuthService,
    @Optional() @Inject(NZ_MODAL_DATA) modalData?: Partial<AccountsDetailsComponent>,
  ) {
    assignModalData(this, modalData);
  }

  ngOnInit() {
    console.log('ACCOUNT DETAIL: ', this);
    this.form.rolesHandler = (role) => this.onRoleChange(role);
    this.auth.getCurrentUserData().subscribe(
      (u) => {
        this.currentUser = u;
        this.isVendorAdmin = this.isVendorAdminUser(u);
        this.isCompanyUser = this.isCompanyAccountUser(u);
        this.setupRolesForCreator(u);
        // LEVEL_2 can edit only own account
        if (u?.accessLevel === 'LEVEL_2' && (this.editMode || this.uid)) {
          const targetId = this.account?.adminId || this.uid;
          if (targetId && targetId !== u.adminId) {
            this.notify.createTopMessage(
              'Action is restricted for this user role. Access denied',
              'error',
              2500,
            );
            setTimeout(() => this.modal.close(), 0);
          }
        }
      },
      () => {
        // ignore
      },
    );
    if (this.editMode) {
      this.initEditMode();
    }
    if (this.uid) {
      this.SRV.getAccount(this.uid).then(
        (res) => {
          this.account = res;
          this.initEditMode();
        },
        (error) => {
          console.log('ERROR: ', error);
        },
      );
    }
  }
}
